import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {enqueueCommand, fetchCommands} from '@/entities/device/api';
import type {DeviceCommandRecord, HalStatus} from '@/entities/device/types';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {ListRow, RowGroup} from '@/shared/ui/list-row';
import {SectionHeader} from '@/shared/ui/section';
import {Segmented} from '@/shared/ui/segmented';
import {AppText} from '@/shared/ui/text';
import {StatusPill, type StatusTone} from '@/shared/ui/status';
import {spacing} from '@/shared/ui/theme';
import {formatTimestamp} from '@/shared/lib/format';

/**
 * Live hardware status + remote control. Commands are queued in the cloud and executed
 * on the terminal's NEXT sync — up to the poll interval later — so the panel is honest
 * about that: a fresh command reads "отправлена", and the status block is labelled "как
 * при последней синхронизации", never "сейчас".
 */
const styles = StyleSheet.create({
  root: {gap: spacing(5)},
  block: {gap: spacing(2)},
  statusGrid: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  buttons: {gap: spacing(2)},
  buttonRow: {flexDirection: 'row', gap: spacing(2)},
  flex: {flex: 1},
  relay: {gap: spacing(3)},
});

/** Длительности импульса, секунды. Прошивка зажимает `durationMs` в [100, 10000]. */
const PULSE_SECONDS = ['1', '3', '5', '10'] as const;
type PulseSeconds = (typeof PULSE_SECONDS)[number];

/** acked → teal, failed/expired → red, pending/delivered → amber. */
function commandTone(status: string): StatusTone {
  if (status === 'acked') {
    return 'online';
  }
  if (status === 'failed' || status === 'expired') {
    return 'offline';
  }
  return 'pending';
}

function HalBlock({hal}: {hal: HalStatus}) {
  const {t} = useTranslation();
  const leds = [
    hal.led_red && t('control.ledRed'),
    hal.led_green && t('control.ledGreen'),
    hal.led_blue && t('control.ledBlue'),
    hal.led_ir && t('control.ledIr'),
  ].filter(Boolean) as string[];
  return (
    <View style={styles.statusGrid}>
      <StatusPill
        tone={hal.relay_on ? 'online' : 'neutral'}
        size="sm"
        label={t(hal.relay_on ? 'control.relayOn' : 'control.relayOff')}
      />
      <StatusPill
        tone="neutral"
        size="sm"
        dot={false}
        label={`${t('control.camera')}: ${hal.camera_source.toUpperCase()}`}
      />
      <StatusPill
        tone="neutral"
        size="sm"
        dot={false}
        label={leds.length ? `LED: ${leds.join(', ')}` : t('control.ledOff')}
      />
      {hal.white_percent > 0 ? (
        <StatusPill
          tone="neutral"
          size="sm"
          dot={false}
          label={`${t('control.white')}: ${hal.white_percent}%`}
        />
      ) : null}
    </View>
  );
}

export function TerminalControlPanel({
  companyId,
  deviceId,
  hal,
}: {
  companyId: string;
  deviceId: string;
  /** Last-reported HAL snapshot, or null/absent if the terminal never reported one. */
  hal?: HalStatus | null;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();

  const commandsQuery = useQuery({
    queryKey: ['device-commands', companyId, deviceId],
    queryFn: () => fetchCommands(companyId, deviceId),
    // Poll while the panel is open so a queued command's ack shows up on its own.
    refetchInterval: 5000,
  });

  const send = useMutation({
    // Команда уходит в очередь и исполнится не сразу — без этой строки нажатие
    // выглядит как ничего не сделавшее.
    meta: {toast: 'toast.deviceCommand', toastError: true},
    mutationFn: (input: {kind: string; params?: Record<string, unknown>}) =>
      enqueueCommand(companyId, deviceId, input.kind, input.params ?? {}),
    onSuccess: () => {
      void qc.invalidateQueries({queryKey: ['device-commands', companyId, deviceId]});
    },
  });

  const [pulse, setPulse] = useState<PulseSeconds>('3');
  const otherCamera = hal?.camera_source === 'ir' ? 'rgb' : 'ir';
  const commands: DeviceCommandRecord[] = commandsQuery.data?.commands ?? [];

  return (
    <View style={styles.root}>
      {hal ? (
        <View style={styles.block}>
          <SectionHeader title={t('control.statusAsOfSync')} />
          <Card padding={4}>
            <HalBlock hal={hal} />
          </Card>
        </View>
      ) : (
        <AppText variant="caption">{t('control.noStatus')}</AppText>
      )}

      <View style={styles.block}>
        <SectionHeader title={t('control.relayTitle')} />
        <Card padding={4} style={styles.relay}>
          {/* The one command an operator opens this panel to send, at the size that
              says so — the rest are secondary. */}
          <AppButton
            title={t('control.openDoor')}
            size="lg"
            icon="lock"
            onPress={() => send.mutate({kind: 'open_door'})}
            loading={send.isPending}
          />
          <AppText variant="label">{t('control.pulseLabel')}</AppText>
          <View style={styles.buttonRow}>
            <Segmented
              options={PULSE_SECONDS.map(s => ({
                value: s,
                label: t('control.seconds', {count: Number(s)}),
              }))}
              value={pulse}
              onChange={setPulse}
              style={styles.flex}
            />
            <AppButton
              title={t('control.pulseAction')}
              tone="neutral"
              disabled={send.isPending}
              onPress={() =>
                send.mutate({
                  kind: 'pulse_relay',
                  params: {durationMs: Number(pulse) * 1000},
                })
              }
            />
          </View>
          <View style={styles.buttonRow}>
            {/* set_relay — удержание без срока: дверь стоит открытой до «Закрыть».
                Закрыть прошивка даёт всегда, даже при остановленной лицензии. */}
            <AppButton
              title={t('control.holdOpen')}
              tone="neutral"
              icon="unlock"
              style={styles.flex}
              disabled={send.isPending}
              onPress={() => send.mutate({kind: 'set_relay', params: {on: true}})}
            />
            <AppButton
              title={t('control.closeRelay')}
              tone="neutral"
              icon="lock"
              style={styles.flex}
              disabled={send.isPending}
              onPress={() => send.mutate({kind: 'set_relay', params: {on: false}})}
            />
          </View>
          <AppText variant="caption">{t('control.holdHint')}</AppText>
        </Card>
      </View>

      <View style={styles.buttons}>
        <View style={styles.buttonRow}>
          <AppButton
            title={t('control.switchCamera', {source: otherCamera.toUpperCase()})}
            tone="neutral"
            style={styles.flex}
            disabled={send.isPending}
            onPress={() => send.mutate({kind: 'switch_camera', params: {source: otherCamera}})}
          />
        </View>
        <View style={styles.buttonRow}>
          <AppButton
            title={t('control.ledGreenOn')}
            tone="neutral"
            style={styles.flex}
            disabled={send.isPending}
            onPress={() => send.mutate({kind: 'set_led', params: {green: true}})}
          />
          <AppButton
            title={t('control.ledOffAction')}
            tone="neutral"
            style={styles.flex}
            disabled={send.isPending}
            onPress={() =>
              send.mutate({
                kind: 'set_led',
                params: {red: false, green: false, blue: false, ir: false, white: 0},
              })
            }
          />
        </View>
        <AppText variant="caption">{t('control.latencyHint')}</AppText>
      </View>

      {commands.length > 0 ? (
        <View style={styles.block}>
          <SectionHeader title={t('control.history')} />
          <RowGroup>
            {commands.map(cmd => (
              <ListRow
                key={cmd.id}
                title={t(`control.kind.${cmd.kind}`, {defaultValue: cmd.kind})}
                subtitle={formatTimestamp(cmd.created_at)}
                chevron={false}
                trailing={
                  <StatusPill
                    tone={commandTone(cmd.status)}
                    size="sm"
                    label={t(`control.state.${cmd.status}`, {defaultValue: cmd.status})}
                  />
                }
              />
            ))}
          </RowGroup>
        </View>
      ) : null}
    </View>
  );
}
